<template>
  <div :class="editorClass" v-if="editor">
    <LazyMenuBar class="editor__header" :editor="editor" :is-code-view-active="isCodeViewActive" :is-maximized="isMaximized" :text-styles-enabled="textStylesEnabled" :embeds-enabled="embedsEnabled" :content-links-enabled="contentLinksEnabled" :columns-enabled="columnsEnabled" :extra-images="extraImages" :image-base-url="imageBaseUrl" v-on:toggleCodeView="toggleCodeView" v-on:toggleMaximize="toggleMaximize" v-on:add-extra-image="$emit('add-extra-image', $event)" />
    <ImageBubbleMenu v-if="editor && !isCodeViewActive" :editor="editor" />
    <EditorContent class="editor__content" :editor="editor" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" v-if="!isCodeViewActive" />
    <textarea v-else :class="codeClass" v-model="text"></textarea>
    <div v-if="isUploadingPaste" class="editor__upload-overlay">{{ _('Uploading image...') }}</div>
    <p v-if="showCookiebotDisclaimer" class="text-info"><i class="fa fa-fw fa-info"></i> {{ _('Cookie management is required to use script, img or iframe tags.') }}</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import { JohkuTable, JohkuTableRow, JohkuTableCell, JohkuTableHeader } from '~/lib/tiptap/JohkuTable'
import { TextStyleKit } from '@tiptap/extension-text-style'
import Iframe from '~/lib/tiptap/IframeExtension'
import { Columns, Column } from '~/lib/tiptap/ColumnsExtension'
import Div from '~/lib/tiptap/DivExtension'
import Span from '~/lib/tiptap/SpanExtension'
import JohkuHtmlAttrs from '~/lib/tiptap/JohkuHtmlAttrs'
import JohkuImage, { johkuImageAttrs } from '~/lib/tiptap/JohkuImage'
import JohkuLink from '~/lib/tiptap/JohkuLink'
import { johkuEmbedClass, johkuEmbedDisplaySrc } from '~/lib/tiptap/johkuEmbedImage'
import { Editor, EditorContent } from '@tiptap/vue-3'
import beautify from 'js-beautify'
import axios from 'axios'
import settingshelpers from '~/lib/settingshelpers'
import errors from '~/lib/errors'
import ImageBubbleMenu from '~/components/tiptap/ImageBubbleMenu.vue'

const MAX_EXTRA_IMAGES = 20
const MAX_PASTE_BYTES = 15 * 1024 * 1024

function clipboardImageFiles(event) {
  const files = []
  const data = event.clipboardData || event.dataTransfer
  if (!data) {
    return files
  }
  const seen = new Set()
  const addFile = (file) => {
    if (!file || !file.type || !file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      return
    }
    const key = file.name + ':' + file.size + ':' + file.lastModified
    if (seen.has(key)) {
      return
    }
    seen.add(key)
    files.push(file)
  }
  if (data.files && data.files.length) {
    Array.from(data.files).forEach(addFile)
  }
  if (!files.length && data.items) {
    Array.from(data.items).forEach((item) => {
      if (item.kind === 'file') {
        addFile(item.getAsFile())
      }
    })
  }
  if (!files.length && typeof data.getData === 'function') {
    const html = data.getData('text/html') || ''
    const re = /src=["'](data:image\/(?:png|jpeg|jpg|gif|webp);base64,[^"']+)["']/gi
    let match
    while ((match = re.exec(html)) !== null) {
      const file = dataUrlToFile(match[1])
      if (file) {
        files.push(file)
      }
    }
  }
  return files
}

function dataUrlToFile(dataUrl) {
  const match = /^data:(image\/(?:png|jpeg|jpg|gif|webp));base64,(.+)$/i.exec(dataUrl)
  if (!match) {
    return null
  }
  const mime = match[1].toLowerCase() === 'image/jpg' ? 'image/jpeg' : match[1]
  const binary = atob(match[2])
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  const ext = mime.split('/')[1] === 'jpeg' ? 'jpg' : mime.split('/')[1]
  return new File([bytes], 'clipboard.' + ext, { type: mime })
}

function pasteFilename(file) {
  const raw = (file && file.name) || ''
  if (raw && raw !== 'image.png' && raw !== 'image.jpg' && raw !== 'image.jpeg') {
    return raw
  }
  const ext = ((file && file.type) || 'image/png').split('/')[1] || 'png'
  return 'clipboard.' + (ext === 'jpeg' ? 'jpg' : ext)
}

export default {
  components: {
    EditorContent,
    ImageBubbleMenu,
  },

  mixins: [settingshelpers, errors],

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    extraImages: {
      type: Array,
      default: null,
    },
    embedsEnabled: {
      type: Boolean,
      default: false,
    },
    contentLinksEnabled: {
      type: Boolean,
      default: true,
    },
    columnsEnabled: {
      type: Boolean,
      default: false,
    },
    // (file, name) => { promise } that uploads through the background queue
    // (lib/uploadaware.js queueUpload). The owning editor then adds the image
    // to its extra images itself.
    queueUpload: {
      type: Function,
      default: null,
    },
  },

  emits: ['update:modelValue', 'add-extra-image'],

  data() {
    return {
      editor: null,
      isCodeViewActive: false,
      isMaximized: false,
      showCookiebotDisclaimer: false,
      isUploadingPaste: false,
      queuedPasteCount: 0,
      pasteSequence: 0
    }
  },

  computed: {
    ...mapGetters(['shopId', 'locale', 'allSettings', 'baseDomain', 'apiKey']),
    settings() {
      return this.shopSettings(this.shopId)
    },
    imageBaseUrl() {
      if (!this.baseDomain || !this.shopId) {
        return ''
      }
      return this.baseDomain + this.shopId
    },
    textStylesEnabled() {
      return this.isFeatureEnabled('textstyles')
    },
    text: {
      get () {
        return this.modelValue
      },
      set (newVal) {
        this.$emit('update:modelValue', newVal)
      }
    },
    editorClass () {
      return 'editor' + (this.isMaximized ? ' editor__maximized' : '') + (this.isUploadingPaste ? ' editor__uploading' : '')
    },
    codeClass () {
      return 'editor__code' + (this.isMaximized ? ' editor__maximized' : '')
    }
  },

  methods: {
    shopSettings(shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    toggleCodeView () {
      this.$emit('update:modelValue', beautify.html(this.text, {
        html: {
          extra_liners: 'p'
        }
      }))
      this.isCodeViewActive = !this.isCodeViewActive
    },
    toggleMaximize () {
      this.isMaximized = !this.isMaximized
    },
    updateCookiebotDisclaimer(html) {
      if (!html) {
        this.showCookiebotDisclaimer = false
        return
      }
      const hasScript = /<script[\s>]/i.test(html)
      const hasIframe = /<iframe[\s>]/i.test(html)
      const hasNonEmbedImg = /<img\b(?![^>]*johku-embed-image)/i.test(html)
      this.showCookiebotDisclaimer = hasScript || hasIframe || hasNonEmbedImg
    },
    clipboardImagesFromEvent(event) {
      return clipboardImageFiles(event)
    },
    onEditorImagePaste(event) {
      if (this.extraImages == null || this.isCodeViewActive) {
        return false
      }
      const files = this.clipboardImagesFromEvent(event)
      if (!files.length) {
        return false
      }
      event.preventDefault()
      if (this.queueUpload) {
        this.queueAndEmbedImages(files)
      } else {
        this.uploadAndEmbedImages(files)
      }
      return true
    },
    onEditorImageDrop(view, event, moved) {
      if (moved || this.extraImages == null || this.isCodeViewActive || !event.dataTransfer) {
        return false
      }
      const files = Array.from(event.dataTransfer.files || []).filter(file => file.type && file.type.startsWith('image/'))
      if (!files.length) {
        return false
      }
      event.preventDefault()
      // Insert where the files were dropped
      const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })
      if (pos) {
        this.editor.commands.setTextSelection(pos.pos)
      }
      if (this.queueUpload) {
        this.queueAndEmbedImages(files)
      } else {
        this.uploadAndEmbedImages(files)
      }
      return true
    },
    // Shows each image at once from the local file and swaps in the uploaded
    // file when its background upload is done, so editing can go on meanwhile
    queueAndEmbedImages(files) {
      const extras = Array.isArray(this.extraImages) ? this.extraImages : []
      const remaining = MAX_EXTRA_IMAGES - extras.length - this.queuedPasteCount
      if (files.length > remaining) {
        this.showWarning(this._('A maximum of 20 additional images is allowed.'))
      }
      for (const file of files.slice(0, Math.max(0, remaining))) {
        if (file.size > MAX_PASTE_BYTES) {
          this.showError(this._('The image is too large.'))
          continue
        }
        const name = pasteFilename(file)
        const uploadId = 'paste-' + (++this.pasteSequence)
        const previewUrl = URL.createObjectURL(file)
        this.editor.chain().focus().insertContent({
          type: 'image',
          attrs: {
            src: previewUrl,
            align: 'left',
            size: 'm',
            class: johkuEmbedClass('left', 'm'),
            uploadId
          },
        }).run()
        this.queuedPasteCount++
        const { promise } = this.queueUpload(file, name)
        promise
          .then(uploaded => this.replaceUploadPlaceholder(uploadId, {
            id: uploaded.id,
            title: (uploaded.filename || name).replace(/\.[^.]+$/, '')
          }))
          .catch(() => this.replaceUploadPlaceholder(uploadId, null))
          .finally(() => {
            this.queuedPasteCount--
            URL.revokeObjectURL(previewUrl)
          })
      }
    },
    replaceUploadPlaceholder(uploadId, uploaded) {
      if (!this.editor || this.editor.isDestroyed) {
        return
      }
      let found = null
      this.editor.state.doc.descendants((node, pos) => {
        if (found === null && node.type.name === 'image' && node.attrs.uploadId === uploadId) {
          found = { node, pos }
        }
        return found === null
      })
      if (!found) {
        return
      }
      const tr = this.editor.state.tr
      if (uploaded) {
        const align = found.node.attrs.align || 'left'
        const size = found.node.attrs.size || 'm'
        tr.setNodeMarkup(found.pos, undefined, {
          ...johkuImageAttrs(uploaded, johkuEmbedDisplaySrc(this.imageBaseUrl, uploaded.id, 800), align, size),
          uploadId: null
        })
      } else {
        tr.delete(found.pos, found.pos + found.node.nodeSize)
      }
      this.editor.view.dispatch(tr)
    },
    async uploadAndEmbedImages(files) {
      if (this.isUploadingPaste) {
        return
      }
      const extras = Array.isArray(this.extraImages) ? this.extraImages : []
      if (extras.length >= MAX_EXTRA_IMAGES) {
        this.showWarning(this._('A maximum of 20 additional images is allowed.'))
        return
      }
      const remaining = MAX_EXTRA_IMAGES - extras.length
      const toUpload = files.slice(0, remaining)
      if (files.length > remaining) {
        this.showWarning(this._('A maximum of 20 additional images is allowed.'))
      }
      this.isUploadingPaste = true
      try {
        for (const file of toUpload) {
          if (file.size > MAX_PASTE_BYTES) {
            this.showError(this._('The image is too large.'))
            continue
          }
          const uploaded = await this.uploadPastedImage(file)
          if (!uploaded || !this.editor) {
            continue
          }
          this.$emit('add-extra-image', uploaded)
          this.editor.chain().focus().insertContent({
            type: 'image',
            attrs: johkuImageAttrs(
              uploaded,
              johkuEmbedDisplaySrc(this.imageBaseUrl, uploaded.id, 800),
              'left',
              'm'
            ),
          }).run()
        }
      } finally {
        this.isUploadingPaste = false
      }
    },
    async uploadPastedImage(file) {
      const formData = new FormData()
      formData.append('files[]', file, pasteFilename(file))
      try {
        const { data } = await axios.post(
          this.baseDomain + this.shopId + '/files/new.json?apikey=' + this.apiKey,
          formData
        )
        if (!data || !data.success || !data.id) {
          this.showError(data, this._('Could not upload the image.'))
          return null
        }
        return {
          id: data.id,
          title: (data.filename || pasteFilename(file)).replace(/\.[^.]+$/, ''),
          filename: data.filename,
        }
      } catch (err) {
        this.showError(err, this._('Could not upload the image.'))
        return null
      }
    }
  },
  watch: {
    text(value) {
      // HTML
      const isSame = this.editor.getHTML() === value

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      this.updateCookiebotDisclaimer(this.editor.getHTML())

      if (isSame) {
        return
      }
      this.editor.commands.setContent(value, {
        emitUpdate: false,
        parseOptions: { preserveWhitespace: true },
      })
      if (!this.isCodeViewActive) {
//        this.editor.commands.focus('start')
        this.editor.commands.blur()
      }

    },
  },

  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit.configure({
          link: false,
        }),
        JohkuLink,
        TextStyleKit,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right']
        }),
        JohkuImage.configure({
          inline: true,
          resize: false,
          imageBaseUrl: this.imageBaseUrl,
        }),
        JohkuTable.configure({
          resizable: true,
        }),
        JohkuTableRow,
        JohkuTableHeader,
        JohkuTableCell,
        Iframe,
        Columns,
        Column,
        Div,
        Span,
        JohkuHtmlAttrs,
      ],
      parseOptions: {
        preserveWhitespace: true
      },
      autofocus: false,
      content: this.text,
      editorProps: {
        handlePaste: (_view, event) => this.onEditorImagePaste(event),
        handleDrop: (view, event, _slice, moved) => this.onEditorImageDrop(view, event, moved),
      },
      onUpdate: () => {
        // HTML
        this.$emit('update:modelValue', this.editor.getHTML())
        // JSON
        // this.$emit('update:modelValue', this.editor.getJSON())
      },
    })
  },

  beforeUnmount() {
    this.editor?.destroy()
  },
}

</script>

<style>
@media (pointer: coarse) {
  .menu-item:not(.is-active):hover {
    border: none !important;
    background-color: transparent !important;
  }
}
.editor {
  display: flex;
  flex-direction: column;
  min-height: 20rem;
  color: #0D0D0D;
  background-color: #FFF;
  border: 1px solid #cfd8dc;
  border-radius: 0;
  position: relative;
}
.editor__upload-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  z-index: 4;
  font-weight: 600;
}
.editor__maximized {
  height: 50rem !important;
}
.editor__code {
  min-height: 20rem;
  border: 0;
  border-radius: 0;
  font-family: monospace;
  resize: vertical;
}
.editor__header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: wrap;
  padding: 0.25rem;
  border-bottom: 1px solid #cfd8dc;
  background-color: #f8f8f8;
}
.editor__content {
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1rem;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  resize: vertical;
  .ProseMirror {
    flex: 1 1 auto;
  }
}
.editor__footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  white-space: nowrap;
  border-top: 1px solid #cfd8dc;
  font-size: 12px;
  font-weight: 600;
  color: #0D0D0D;
  white-space: nowrap;
  padding: 0.25rem 0.75rem;
  /* Some information about the status */
}
.editor__status {
  display: flex;
  align-items: center;
  border-radius: 5px;
}
.editor__status::before {
  content: ' ';
  flex: 0 0 auto;
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  background: rgba(13, 13, 13, 0.5);
  border-radius: 50%;
  margin-right: 0.5rem;
}
.editor__status--connecting::before {
  background: #616161;
}
.editor__status--connected::before {
  background: #B9F18D;
}
.editor__name button {
  background: none;
  border: none;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #0D0D0D;
  border-radius: 0.4rem;
  padding: 0.25rem 0.5rem;
}
.editor__name button:hover {
  color: #FFF;
  background-color: #0D0D0D;
}
.johku-embed-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin: 0.75rem 0;
  padding: 1rem;
  border: 1px dashed #90a4ae;
  border-radius: 4px;
  background: #eceff1;
  color: #455a64;
  text-align: center;
  cursor: grab;
}
.johku-embed-placeholder__icon {
  font-size: 1.5rem;
}
.johku-embed-placeholder__label {
  font-weight: 600;
}
.johku-embed-placeholder__detail {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  opacity: 0.85;
}
</style>

<style>
/* Give a remote user a caret */
.collaboration-cursor__caret {
  position: relative;
  margin-left: -1px;
  margin-right: -1px;
  border-left: 1px solid #0D0D0D;
  border-right: 1px solid #0D0D0D;
  word-break: normal;
  pointer-events: none;
}
/* Render the username above the caret */
.collaboration-cursor__label {
  position: absolute;
  top: -1.4em;
  left: -1px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  user-select: none;
  color: #0D0D0D;
  padding: 0.1rem 0.3rem;
  border-radius: 3px 3px 3px 0;
  white-space: nowrap;
}
/* Basic editor styles */
.ProseMirror > * + * {
  margin-top: 0.75em;
}
.ProseMirror:focus {
  outline: none;
}
.ProseMirror ul, .ProseMirror ol {
  padding: 0 1rem;
}
.ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 {
  line-height: 1.1;
}
.ProseMirror h1 {
  font-size: 1.6em;
}
.ProseMirror h2 {
  font-size: 1.3em;
}
.ProseMirror h3 {
  font-size: 1.1em;
}
.ProseMirror h4, .ProseMirror h5, .ProseMirror h6 {
  font-size: 1em;
}
.ProseMirror code {
  background-color: rgba(97, 97, 97, 0.1);
  color: #616161;
}
.ProseMirror pre {
  background: #0D0D0D;
  color: #FFF;
  font-family: 'JetBrainsMono', monospace;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
.ProseMirror pre code {
  color: inherit;
  padding: 0;
  background: none;
  font-size: 0.8rem;
}
.ProseMirror mark {
  background-color: #FAF594;
}
.ProseMirror img {
  max-width: 100%;
  height: auto;
}
.ProseMirror img.johku-embed-image {
  cursor: pointer;
  width: auto;
  height: auto;
  max-width: min(320px, 45%);
}
.ProseMirror img.johku-embed-image--s {
  max-width: min(192px, 30%);
}
.ProseMirror img.johku-embed-image--l {
  max-width: min(576px, 80%);
}
.ProseMirror img.johku-embed-image.ProseMirror-selectednode {
  outline: 2px solid #337ab7;
  outline-offset: 2px;
}
.ProseMirror::after {
  content: "";
  display: table;
  clear: both;
}
.ProseMirror img.johku-embed-image--left {
  float: left;
  margin: 0 1.4rem 0.5rem 0;
}
.ProseMirror img.johku-embed-image--right {
  float: right;
  margin: 0 0 0.5rem 1.4rem;
}
.ProseMirror img.johku-embed-image--center {
  display: block;
  float: none;
  margin: 1rem auto;
}
.ProseMirror img.johku-embed-image--center.johku-embed-image--l {
  max-width: 100%;
}
/* Blocks whose markers, padding, borders or backgrounds would otherwise slide under a floated image. */
.ProseMirror:has(img.johku-embed-image--left, img.johku-embed-image--right) :is(ul, ol, blockquote, hr, div[style*="background"], div[style*="border"]) {
  display: flow-root;
}
.ProseMirror:has(img.johku-embed-image--left, img.johku-embed-image--right) :is(ul, ol) > li:last-child > :last-child {
  margin-bottom: 0;
}
/* Following items wrap beside a float placed inside a list item; reserve the list indent (1rem) for their markers. */
.ProseMirror li img.johku-embed-image--left {
  margin-right: 2.4rem;
}
.ProseMirror hr {
  margin: 1rem 0;
}
.ProseMirror blockquote {
  padding-left: 1rem;
  border-left: 2px solid rgba(13, 13, 13, 0.1);
}
.ProseMirror hr {
  border: none;
  border-top: 2px solid rgba(13, 13, 13, 0.1);
  margin: 2rem 0;
}
.ProseMirror ul[data-type="taskList"] {
  list-style: none;
  padding: 0;
}
.ProseMirror ul[data-type="taskList"] li {
  display: flex;
  align-items: center;
}
.ProseMirror ul[data-type="taskList"] li > label {
  flex: 0 0 auto;
  margin-right: 0.5rem;
  user-select: none;
}
.ProseMirror ul[data-type="taskList"] li > div {
  flex: 1 1 auto;
}

.ProseMirror table {
  margin: 0;
  overflow: hidden;
}

/* Grid, padding and shrink-to-fit only for tables that do not already
   set a size or a border (for example a hairline). Author widths,
   cellpadding and border styles have to win over the editor chrome. */
.ProseMirror table:not([width]):not([class]):not([style^="width:"]):not([style*="; width:"]):not([style^="min-width:"]):not([style*="; min-width:"]):not([style^="max-width:"]):not([style*="; max-width:"]) {
  width: fit-content;
  max-width: 100%;
}
.ProseMirror table:not([cellspacing]):not([class]):not([style*="border-collapse"]):not([style*="border-spacing"]):not(:has(td[style*="border:"], td[style*="border-top"], td[style*="border-right"], td[style*="border-bottom"], td[style*="border-left"], td[style*="border-width"], td[style*="border-style"], th[style*="border:"], th[style*="border-top"], th[style*="border-right"], th[style*="border-bottom"], th[style*="border-left"], th[style*="border-width"], th[style*="border-style"])) {
  border-collapse: collapse;
}
.ProseMirror table td,
.ProseMirror table th {
  position: relative;
}
.ProseMirror table td:not([width]):not([style^="width:"]):not([style*="; width:"]):not([style^="min-width:"]):not([style*="; min-width:"]),
.ProseMirror table th:not([width]):not([style^="width:"]):not([style*="; width:"]):not([style^="min-width:"]):not([style*="; min-width:"]) {
  min-width: 1em;
}
.ProseMirror table:not([border]):not([class]):not([style*="border:"]):not([style*="border-top"]):not([style*="border-right"]):not([style*="border-bottom"]):not([style*="border-left"]):not([style*="border-width"]):not([style*="border-style"]):not([style*="border-color"]) td:not([border]):not([class]):not([style*="border:"]):not([style*="border-top"]):not([style*="border-right"]):not([style*="border-bottom"]):not([style*="border-left"]):not([style*="border-width"]):not([style*="border-style"]):not([style*="border-color"]),
.ProseMirror table:not([border]):not([class]):not([style*="border:"]):not([style*="border-top"]):not([style*="border-right"]):not([style*="border-bottom"]):not([style*="border-left"]):not([style*="border-width"]):not([style*="border-style"]):not([style*="border-color"]) th:not([border]):not([class]):not([style*="border:"]):not([style*="border-top"]):not([style*="border-right"]):not([style*="border-bottom"]):not([style*="border-left"]):not([style*="border-width"]):not([style*="border-style"]):not([style*="border-color"]) {
  border: 2px solid #ced4da;
}
.ProseMirror table:not([cellpadding]):not([class]) td:not([class]):not([style*="padding"]),
.ProseMirror table:not([cellpadding]):not([class]) th:not([class]):not([style*="padding"]) {
  padding: 3px 5px;
}
.ProseMirror table td:not([valign]):not([style*="vertical-align"]),
.ProseMirror table th:not([valign]):not([style*="vertical-align"]) {
  vertical-align: top;
}

.ProseMirror table td > *,
.ProseMirror table th > * {
  margin-bottom: 0;
}

.ProseMirror table th:not([class]):not([style*="font-weight"]):not([style*="font:"]) {
  font-weight: bold;
}
.ProseMirror table th:not([align]):not([class]):not([style*="text-align"]) {
  text-align: left;
}
.ProseMirror table th:not([bgcolor]):not([class]):not([style*="background"]) {
  background-color: #f1f3f5;
}

.ProseMirror table .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0; right: 0; top: 0; bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}

.ProseMirror table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 4px;
  background-color: #adf;
  pointer-events: none;
}

.ProseMirror table p {
  margin: 0;
}

.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.ProseMirror .johku-columns {
  display: grid;
  gap: 0.75rem;
  margin: 0.75em 0;
  width: 100%;
}
.ProseMirror .johku-columns--2 {
  grid-template-columns: 1fr 1fr;
}
.ProseMirror .johku-columns--3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.ProseMirror .johku-column {
  min-width: 0;
  border: 1px dashed #90a4ae;
  border-radius: 4px;
  padding: 0.5rem 0.65rem;
  background: rgba(236, 239, 241, 0.35);
}
.ProseMirror .johku-column--span-2 {
  grid-column: span 2;
}
.ProseMirror .johku-column > * + * {
  margin-top: 0.75em;
}
.ProseMirror .johku-column > :first-child {
  margin-top: 0;
}
</style>